import { Alert, Card, Group, Stack, Text } from '@mantine/core';
import { IconClockExclamation } from '@tabler/icons-react';
import type { Resumen } from '@caes-payments/shared';

import { etiquetaBucketBlacklist } from '../blacklist';
import { describirMinimo, etaTeoricoSegundos } from '../eta';
import { formatearEur, formatearNumero } from '../formato';
import { describirFrescura } from '../frescura';
import type { DistribucionId } from '../programas';
import { Distribucion, type FilaDistribucion } from './Distribucion';

/** Estados donde una seleccion amplia hace mas dano. */
const ESTADOS_PELIGROSOS = ['CANCELED', 'FAILED'];

/**
 * Comprobacion de cuadre.
 *
 * NO es agregar para mostrar: la cifra que se pinta siempre es `importeTotalEur` del backend. Es una
 * red que avisa cuando los propios numeros del backend no cuadran entre si — tipicamente por un
 * bucket que se quedo fuera del desglose. Sin esto, un desglose incompleto se ve normal.
 */
function descuadra(total: string, filas: FilaDistribucion[]): boolean {
  const suma = filas.reduce((acc, f) => acc + Number(f.importeEur), 0);
  return Math.abs(suma - Number(total)) > 0.01;
}

/**
 * El resumen de lo que se pagaría con el filtro aplicado: la cifra, y su distribución.
 *
 * Desde el 2026-09-10 (BB-2000) el resumen SOLO enseña: el filtro es lo de arriba, y nada de aquí se
 * puede clicar para acotar. Antes había una tabla CA × semana que era a la vez visualización y filtro;
 * se quitó porque Ops necesitaba que filtrar fuera una sola cosa, en un solo sitio, y que lo de
 * abajo respondiera "qué contiene ese filtro" sin más.
 *
 * Una distribución por cada dimensión filtrable del programa —las dice `distribuciones`, de
 * `programas.ts`— más la de estado de pago, que es trivial por el invariante 2 y se mantiene como
 * comprobación de coherencia. Todas salen de los agregados del backend tal cual: el front no suma.
 */
export function ResumenPanel({
  resumen,
  etiquetaAlcance,
  datosActualizadosEn,
  limiteRpsPago,
  distribuciones,
  verboCifra = 'Se pagaría',
}: {
  resumen: Resumen;
  etiquetaAlcance: string;
  /** Qué distribuciones pintar y en qué orden. Lo decide el programa, no la forma del resumen. */
  distribuciones: readonly DistribucionId[];
  /**
   * Cómo se presenta la cifra grande. "Se pagaría" solo cuando de verdad se puede confirmar desde
   * esta pantalla; en un programa de solo revisión decir "se pagaría" prometería un botón que no hay.
   */
  verboCifra?: string;
  /**
   * Ritmo del endpoint de pago (`limiteRpsPago` del contrato, BB-1651). Con él se dice cuánto tardaría
   * confirmar esta selección ANTES de pulsar: a una petición por viaje, una selección grande son
   * horas, y eso cambia la decisión de Ops tanto como el importe.
   */
  limiteRpsPago: number | undefined;
  /**
   * Cuándo escribió dbt la tabla origen (BB-1627). Llega del MISMO preview que estas cifras, así que
   * describe los datos que se están viendo y no el reloj: si la pantalla muestra un preview de hace
   * veinte minutos, esto también es de entonces. Es lo coherente con que el preview sea un snapshot
   * — que nadie lo convierta en un contador en vivo.
   */
  datosActualizadosEn: string | null | undefined;
}) {
  // El contrato tipa cada desglose por su dimension; la UI los pinta igual. Aqui se normalizan.
  const porCa: FilaDistribucion[] = resumen.porCa.map((f) => ({
    clave: f.regionName,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));
  const porEstadoPago: FilaDistribucion[] = resumen.porPaymentState.map((f) => ({
    clave: f.caesPaymentState,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));
  const porMiteco: FilaDistribucion[] = resumen.porMitecoStatus.map((f) => ({
    clave: f.caesMitecoStatus,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));
  // Nunca NULL: dbt pone 'No Certificados MITECO' a los viajes sin fichero, y ese string es un valor
  // mas de la lista (y hoy el mayor, con diferencia). Por eso aqui no hay bucket "sin valor".
  const porFichero: FilaDistribucion[] = resumen.porDriveFileName.map((f) => ({
    clave: f.driveFileName,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));
  const porPeriodo: FilaDistribucion[] = resumen.porCertificationPeriod.map((f) => ({
    clave: f.certificationPeriod,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));
  // Solo los rellena el programa softKiller; en elegibles llegan como [] (o ausentes, en resumenes
  // anteriores al contrato de BB-2022).
  const porMotivo: FilaDistribucion[] = (resumen.porSoftKillerReason ?? []).map((f) => ({
    clave: f.caesSoftKillerReason,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));
  const porOrigen: FilaDistribucion[] = (resumen.porSoftKillerOrigen ?? []).map((f) => ({
    clave: f.caesSoftKillerOrigen,
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));

  // BB-2607/BB-2608. Ausente (no `[]`) = backend sin desplegar todavía: entonces la tarjeta NO se
  // pinta, en vez de enseñar "0 valores" como si el conjunto no tuviera conductores en blacklist.
  const porBlacklist: FilaDistribucion[] | undefined = resumen.porIsDriverBlacklisted?.map((f) => ({
    clave: etiquetaBucketBlacklist(f.isDriverBlacklisted),
    nViajes: f.nViajes,
    importeEur: f.importeEur,
  }));

  const catalogo: Record<DistribucionId, { titulo: string; filas: FilaDistribucion[] | undefined }> = {
    ca: { titulo: 'Por comunidad autonoma', filas: porCa },
    miteco: { titulo: 'Por estado MITECO', filas: porMiteco },
    periodo: { titulo: 'Por periodo de certificacion MITECO', filas: porPeriodo },
    fichero: { titulo: 'Por fichero de certificacion MITECO', filas: porFichero },
    motivo: { titulo: 'Por motivo del soft killer', filas: porMotivo },
    origen: { titulo: 'Por origen del rechazo', filas: porOrigen },
    blacklist: { titulo: 'Por conductor en blacklist', filas: porBlacklist },
    pago: { titulo: 'Por estado de pago', filas: porEstadoPago },
  };

  const hayDescuadre =
    descuadra(resumen.importeTotalEur, porCa) || descuadra(resumen.importeTotalEur, porEstadoPago);

  // Se nombran individualmente, NO sumados: el aviso no depende de que Ops llegue hasta la
  // distribucion de estados scrolleando.
  const peligrosos = porEstadoPago.filter(
    (f) => f.clave !== null && ESTADOS_PELIGROSOS.includes(f.clave) && Number(f.importeEur) > 0,
  );

  // `Date.now()` aquí y no en el módulo: la funcion es pura y recibe el reloj, para poder testearla.
  const frescura = describirFrescura(datosActualizadosEn, Date.now());

  const segundosMinimos = etaTeoricoSegundos(resumen.nViajes, limiteRpsPago);

  return (
    <Stack gap="md">
      {/* Encima de la cifra a proposito: es el dato que decide si se puede confiar en ella. Misma
          posicion siempre —aviso o no— para que Ops aprenda donde mirar; lo que cambia es el peso. */}
      {frescura.esAviso ? (
        <Alert
          color="orange"
          icon={<IconClockExclamation size={18} />}
          title={frescura.texto}
        >
          {frescura.queHacer}
        </Alert>
      ) : (
        <Text size="xs" c="dimmed">
          {frescura.texto}
        </Text>
      )}

      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed">
          {verboCifra} · {etiquetaAlcance}
        </Text>
        <div className="importe-total">{formatearEur(resumen.importeTotalEur)}</div>
        <Group gap="md" mt="xs">
          <Text size="sm">{formatearNumero(resumen.nViajes)} viajes</Text>
          {resumen.nViajesSinImporte > 0 && (
            <Text size="sm" c="dimmed">
              · {formatearNumero(resumen.nViajesSinImporte)} sin importe
            </Text>
          )}
          {resumen.rangoFechasEfectivo && (
            <Text size="sm" c="dimmed">
              · {resumen.rangoFechasEfectivo.min} a {resumen.rangoFechasEfectivo.max}
            </Text>
          )}
        </Group>

        {/* "Al menos", nunca "tardará": es el suelo teórico (viajes / ritmo del endpoint). El cliente
            de pago va secuencial, así que la latencia de cada POST se suma encima, y los reintentos
            solo pueden alargarlo. Prometer una cifra exacta aquí seria mentir sobre una espera de
            horas. */}
        {segundosMinimos !== null && (
          <Text size="sm" c="dimmed" mt="xs">
            Confirmar esto tardaría {describirMinimo(segundosMinimos)}: el endpoint de pago
            se llama una vez por viaje, a {formatearNumero(limiteRpsPago ?? 0)} por segundo.
          </Text>
        )}
      </Card>

      {hayDescuadre && (
        <Alert color="red" title="Los desgloses no cuadran con el total">
          La suma por CA o por estado de pago no coincide con el importe total del backend. No
          confirmes: falta algun bucket en el desglose.
        </Alert>
      )}

      {peligrosos.length > 0 && (
        <Alert color="orange" title="La seleccion incluye estados que no suelen pagarse">
          {peligrosos.map((f) => (
            <Text key={f.clave} size="sm">
              {f.clave}: {formatearEur(f.importeEur)} en {formatearNumero(f.nViajes)} viajes
            </Text>
          ))}
          <Text size="sm" mt="xs" c="dimmed">
            El estado de pago no es filtrable, asi que no se pueden excluir desde aqui. Si no deben
            entrar, hay que acotar por fecha, CA, estado MITECO, certificacion o conductor en
            blacklist.
          </Text>
        </Alert>
      )}

      {/* Dos columnas: cada dimension que se puede filtrar arriba tiene aqui su distribucion, en el
          orden que fija el programa. La de estado de pago cierra: no es filtrable y casi siempre es
          un unico bucket. */}
      <div className="rejilla-distribuciones">
        {distribuciones.map((id) => {
          const { titulo, filas } = catalogo[id];
          if (filas === undefined) return null;
          return (
            <Distribucion key={id} titulo={titulo} filas={filas} totalEur={resumen.importeTotalEur} />
          );
        })}
      </div>
    </Stack>
  );
}
